import {
  prefetchCsrfToken,
  clearCachedCsrfToken,
  getCachedCsrfToken,
} from "@/utils/csrfTokenCache";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://tellerwand.com";

export const CALCULATOR_SERVICE_URL =
  process.env.NODE_ENV === "production"
    ? `${API_BASE_URL}/api/v1/calculator`
    : "http://localhost:3008";

export const CALCULATOR_FETCH_TIMEOUT_MS = 30_000;

/** User-safe message — never surface raw CSRF errors (TW-13). */
export function toUserFacingCalculatorError(raw: string): string {
  if (/csrf/i.test(raw)) {
    return "Something went wrong. Please refresh the page and try again.";
  }
  if (/too many (calculation )?requests/i.test(raw) || /\b429\b/.test(raw)) {
    return "Too many calculations just now. Please wait a minute and try again.";
  }
  return raw.trim() || "Calculation failed";
}

export async function buildCalculatorRequestHeaders(): Promise<globalThis.HeadersInit> {
  const csrfToken = await prefetchCsrfToken(API_BASE_URL);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }
  return headers;
}

async function parseCalculatorError(response: Response): Promise<string> {
  const errorData = await response.json().catch(() => ({ error: response.statusText }));
  const raw = typeof errorData.error === "string" ? errorData.error : "Calculation failed";
  return toUserFacingCalculatorError(raw);
}

/**
 * POST to calculator-service via the API gateway.
 * Retries once on 403 after clearing a stale cached CSRF token (same pattern as BaseService).
 */
export async function postCalculatorApi<T>(
  endpoint: string,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<T> {
  const url = `${CALCULATOR_SERVICE_URL}${endpoint}`;
  const payload = JSON.stringify(body);

  const doFetch = async () =>
    fetch(url, {
      method: "POST",
      headers: await buildCalculatorRequestHeaders(),
      credentials: "include",
      body: payload,
      signal,
    });

  let response = await doFetch();

  if (response.status === 403 && getCachedCsrfToken()) {
    clearCachedCsrfToken();
    response = await doFetch();
  }

  if (!response.ok) {
    throw new Error(await parseCalculatorError(response));
  }

  return response.json() as Promise<T>;
}
